// src/modules/workspace/CorrelationLab.jsx (v2)
// ☆ PIN TO DASHBOARD on: PairCard, scatter gallery cards, heatmap deep-dive
import React, { useState, useMemo, useEffect } from 'react';
import { C, PALETTE, pearson, inferTypes, colStats, PinButton, LabLoader } from './wsShared';
import { EChart } from './DashboardEngine';

const MAX_SCATTER  = 1_500;
const MAX_CORR_PTS = 2_000;

function sampleRows(rows, max) {
  if (rows.length <= max) return rows;
  const step = Math.ceil(rows.length / max);
  const out = [];
  for (let i = 0; i < rows.length; i += step) out.push(rows[i]);
  return out.slice(0, max);
}

function buildScatterPts(rows, xCol, yCol, max = MAX_SCATTER) {
  const step = rows.length > max ? Math.ceil(rows.length / max) : 1;
  return rows.filter((_, i) => i % step === 0).slice(0, max)
    .map(r => [parseFloat(r[xCol]), parseFloat(r[yCol])])
    .filter(p => !isNaN(p[0]) && !isNaN(p[1]));
}

function normalizeScatterPts(points) {
  if (!Array.isArray(points) || !points.length) return [];
  return points
    .map((point) => {
      if (Array.isArray(point)) {
        const x = parseFloat(point[0]);
        const y = parseFloat(point[1]);
        return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
      }
      if (point && typeof point === 'object') {
        const x = parseFloat(point.x ?? point[0]);
        const y = parseFloat(point.y ?? point[1]);
        return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
      }
      return null;
    })
    .filter(Boolean);
}

const rColor  = r => Math.abs(r)>0.7?(r>0?C.green:C.red):Math.abs(r)>0.4?(r>0?C.cyan:C.orange):C.t3;
const rStrong = r => { const a=Math.abs(r); return (a>0.7?'Strong':a>0.4?'Moderate':'Weak')+(r>0?' positive':r<0?' negative':''); };

// ─── Mini Scatter ─────────────────────────────────────────────────────────────
const MiniScatter = React.memo(function MiniScatter({ rows, xCol, yCol, color=C.cyan, height=120, precomputedPts=null }) {
  const data = useMemo(()=>{
    const normalizedPts = normalizeScatterPts(precomputedPts);
    if (normalizedPts.length) return normalizedPts;
    return buildScatterPts(rows, xCol, yCol, MAX_SCATTER);
  },[rows,xCol,yCol,precomputedPts]);
  const pointSize = height >= 200 ? 9 : height >= 150 ? 7 : 6;
  const option = useMemo(()=>({
    backgroundColor:'transparent',
    tooltip:{
      trigger:'item',
      triggerOn:'mousemove|click',
      backgroundColor:C.void,
      borderColor:C.b2,
      borderWidth:1,
      padding:[8,10],
      textStyle:{color:C.t2,fontSize:9,fontFamily:'JetBrains Mono,monospace'},
      formatter:(params)=>{
        const value = Array.isArray(params?.value) ? params.value : [];
        const x = Number(value[0]);
        const y = Number(value[1]);
        return [
          `<div style="color:${C.t4};font-size:8px;letter-spacing:.08em;margin-bottom:4px;">SCATTER POINT</div>`,
          `<div><span style="color:${C.t4};">${xCol}:</span> <b style="color:${C.t1};">${Number.isFinite(x) ? x.toFixed(4) : '-'}</b></div>`,
          `<div><span style="color:${C.t4};">${yCol}:</span> <b style="color:${C.t1};">${Number.isFinite(y) ? y.toFixed(4) : '-'}</b></div>`,
        ].join('');
      },
    },
    grid:{top:4,right:4,bottom:16,left:4,containLabel:true},
    xAxis:{type:'value',scale:true,axisLabel:{color:C.t4,fontSize:7},axisLine:{show:false},axisTick:{show:false},splitLine:{show:false}},
    yAxis:{type:'value',scale:true,axisLabel:{color:C.t4,fontSize:7},axisLine:{show:false},axisTick:{show:false},splitLine:{lineStyle:{color:C.b0,type:'dashed'}}},
    series:[{
      type:'scatter',
      data,
      symbolSize:pointSize,
      itemStyle:{
        color,
        opacity:0.82,
        borderColor:C.void,
        borderWidth:0.6,
        shadowBlur:4,
        shadowColor:`${color}55`,
      },
      emphasis:{
        scale:true,
        itemStyle:{
          opacity:1,
          borderColor:'#ffffff',
          borderWidth:1.2,
          shadowBlur:10,
          shadowColor:`${color}aa`,
        },
      },
    }]
  }),[data,color,xCol,yCol,pointSize,height]);
  if(!data.length) return null;
  return <EChart height={height} option={option}/>;
});

// ─── Pair Card ────────────────────────────────────────────────────────────────
const PairCard = React.memo(function PairCard({ rows, pair, rank, selected, onSelect }) {
  const { a, b, r } = pair;
  const color = rColor(r);
  const sA = useMemo(()=>colStats(rows,a),[rows,a]);
  const sB = useMemo(()=>colStats(rows,b),[rows,b]);
  const pinChart = { id:`corr_pair_${a}_${b}`, type:'scatter', xCol:a, yCol:b, title:`${a} vs ${b}`, subtitle:`r=${r.toFixed(3)} · correlation` };

  return (
    <div onClick={()=>onSelect(pair)} style={{
      background:C.surface,border:`1px solid ${selected?color:C.b1}`,
      borderLeft:`3px solid ${color}`,borderRadius:4,overflow:'hidden',
      cursor:'pointer',outline:selected?`1px solid ${color+'88'}`:'none',transition:'all .15s',
    }}>
      <div style={{padding:'8px 12px',background:C.deep,borderBottom:`1px solid ${C.b0}`,
        display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
        <div>
          <div style={{fontFamily:'JetBrains Mono,monospace',fontSize:9,color:C.t1,fontWeight:700}}>
            #{rank} {a} ↔ {b}
          </div>
          <div style={{fontSize:8,color:C.t4,marginTop:1}}>{rStrong(r)}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
          <PinButton chart={pinChart} small />
          <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:22,color}}>{r.toFixed(3)}</div>
        </div>
      </div>
      <div style={{height:4,background:C.b1}}>
        <div style={{height:'100%',width:`${Math.abs(r)*100}%`,background:color,transition:'width .3s'}}/>
      </div>
      <div style={{padding:'6px 10px 4px'}}>
        <MiniScatter rows={rows} xCol={a} yCol={b} color={color} height={130} precomputedPts={pair.pts||null}/>
      </div>
      {sA&&sB&&(
        <div style={{display:'flex',borderTop:`1px solid ${C.b0}`}}>
          {[{col:a,s:sA},{col:b,s:sB}].map(({col,s})=>(
            <div key={col} style={{flex:1,padding:'6px 10px',borderRight:`1px solid ${C.b0}`}}>
              <div style={{fontSize:7,color:C.t4,letterSpacing:'.08em',marginBottom:2}}>{col.slice(0,14)}</div>
              <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',lineHeight:1.6}}>
                <div>μ={s.mean}</div><div>σ={s.std}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// ─── Corr Dashboard (positive / negative) ────────────────────────────────────
function CorrDashboard({ rows, allPairs, direction }) {
  const isPos   = direction==='positive';
  const mainCol = isPos?C.green:C.red;
  const accent  = isPos?C.cyan:C.orange;
  const [selPair,setSelPair] = useState(null);

  const pairs = useMemo(()=>
    allPairs.filter(p=>isPos?p.r>0.05:p.r<-0.05)
      .sort((a,b)=>isPos?b.r-a.r:a.r-b.r).slice(0,9)
  ,[allPairs,isPos]);

  const active = selPair||pairs[0];

  if(!pairs.length) return (
    <div style={{padding:40,textAlign:'center',color:C.t4}}>No {direction} correlations detected.</div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      {/* Summary strip */}
      <div style={{display:'flex',gap:8,overflowX:'auto',padding:'2px 0'}}>
        {pairs.map((p,i)=>(
          <div key={i} onClick={()=>setSelPair(p)} style={{
            flexShrink:0,padding:'9px 14px',background:C.surface,
            border:`1px solid ${(selPair||pairs[0])===p?mainCol:C.b1}`,
            borderRadius:4,cursor:'pointer',minWidth:110,
          }}>
            <div style={{fontSize:8,color:C.t4,marginBottom:3,fontFamily:'JetBrains Mono,monospace'}}>
              {p.a.slice(0,8)} × {p.b.slice(0,8)}
            </div>
            <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:20,color:rColor(p.r)}}>{p.r.toFixed(3)}</div>
          </div>
        ))}
      </div>

      {/* Deep dive */}
      {active&&(
        <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
          <div style={{flex:2,minWidth:260,background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,overflow:'hidden'}}>
            <div style={{padding:'9px 14px',background:C.deep,borderBottom:`1px solid ${C.b0}`,
              display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontFamily:'JetBrains Mono,monospace',fontSize:10,color:mainCol,fontWeight:700}}>
                  {active.a} × {active.b}
                </div>
                <div style={{fontSize:8,color:C.t4}}>{rStrong(active.r)}</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <PinButton chart={{ id:`corr_deep_${active.a}_${active.b}`, type:'scatter', xCol:active.a, yCol:active.b, title:`${active.a} vs ${active.b}`, subtitle:`r=${active.r.toFixed(3)}` }} />
                <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:28,color:rColor(active.r)}}>{active.r.toFixed(3)}</div>
              </div>
            </div>
            <div style={{padding:'10px 14px 14px'}}>
              <MiniScatter rows={rows} xCol={active.a} yCol={active.b} color={rColor(active.r)} height={220}/>
            </div>
          </div>

          {/* Strength ranking */}
          <div style={{flex:1,minWidth:220,display:'flex',flexDirection:'column',gap:8}}>
            {pairs.map((p,i)=>(
              <div key={i} onClick={()=>setSelPair(p)} style={{
                display:'flex',alignItems:'center',gap:10,cursor:'pointer',
                padding:'7px 10px',background:C.surface,border:`1px solid ${active===p?accent:C.b1}`,borderRadius:3,
              }}>
                <span style={{fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace',minWidth:14}}>#{i+1}</span>
                <div style={{flex:1,overflow:'hidden'}}>
                  <div style={{fontSize:9,color:C.t1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                    {p.a.slice(0,10)} ↔ {p.b.slice(0,10)}
                  </div>
                  <div style={{height:3,background:C.b1,marginTop:3,borderRadius:2}}>
                    <div style={{height:'100%',width:`${Math.abs(p.r)*100}%`,background:rColor(p.r),borderRadius:2}}/>
                  </div>
                </div>
                <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:11,color:rColor(p.r),fontWeight:700,flexShrink:0}}>{p.r.toFixed(3)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pair cards grid */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:12}}>
        {pairs.map((p,i)=>(
          <PairCard key={i} rows={rows} pair={p} rank={i+1}
            selected={active===p} onSelect={setSelPair}/>
        ))}
      </div>
    </div>
  );
}

// ─── Scatter Gallery ─────────────────────────────────────────────────────────
function ScatterGallery({ rows, allPairs }) {
  const [thresh, setThresh] = useState(0.3);
  const [limit,  setLimit]  = useState(12);

  const filtered = useMemo(()=>
    allPairs.filter(p=>Math.abs(p.r)>=thresh)
      .sort((a,b)=>Math.abs(b.r)-Math.abs(a.r)).slice(0,limit)
  ,[allPairs,thresh,limit]);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div style={{display:'flex',gap:14,alignItems:'center',flexWrap:'wrap',
        padding:'10px 14px',background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:8,color:C.t3,fontFamily:'JetBrains Mono,monospace'}}>MIN |r|</span>
          <input type="range" min={0} max={0.9} step={0.05} value={thresh}
            onChange={e=>setThresh(+e.target.value)} style={{accentColor:C.cyan,width:110}}/>
          <span style={{fontSize:10,color:C.cyan,fontFamily:'JetBrains Mono,monospace',minWidth:30}}>{thresh.toFixed(2)}</span>
        </div>
        <div style={{display:'flex',gap:3}}>
          {[6,12,20,30].map(n=>(
            <button key={n} onClick={()=>setLimit(n)} style={{
              padding:'3px 8px',border:`1px solid ${limit===n?C.cyan:C.b1}`,
              background:limit===n?C.cyan+'20':'transparent',color:limit===n?C.cyan:C.t4,
              fontFamily:'JetBrains Mono,monospace',fontSize:9,borderRadius:2,cursor:'pointer',
            }}>{n}</button>
          ))}
        </div>
        <span style={{fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace'}}>
          {filtered.length} pairs · ☆ to pin any chart
        </span>
      </div>

      {!filtered.length&&(
        <div style={{padding:40,textAlign:'center',color:C.t4}}>No pairs meet threshold — lower slider.</div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:12}}>
        {filtered.map((p,i)=>(
          <div key={i} style={{background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,overflow:'hidden'}}>
            <div style={{padding:'7px 12px',background:C.deep,borderBottom:`1px solid ${C.b0}`,
              display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'JetBrains Mono,monospace',fontSize:9,color:C.t1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:130}}>
                {p.a.slice(0,10)} ↔ {p.b.slice(0,10)}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                <PinButton chart={{ id:`corr_gal_${p.a}_${p.b}`, type:'scatter', xCol:p.a, yCol:p.b, title:`${p.a} vs ${p.b}`, subtitle:`r=${p.r.toFixed(3)} · gallery` }} small />
                <span style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:17,color:rColor(p.r)}}>{p.r.toFixed(3)}</span>
              </div>
            </div>
            <div style={{height:4,background:C.b1}}>
              <div style={{height:'100%',width:`${Math.abs(p.r)*100}%`,background:rColor(p.r)}}/>
            </div>
            <div style={{padding:'6px 10px 8px'}}>
              <MiniScatter rows={rows} xCol={p.a} yCol={p.b} color={rColor(p.r)} height={130} precomputedPts={p.pts||null}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────
export default function CorrelationLab({ ds, precomputedCorrState = null }) {
  const { rows, headers } = ds;
  const [activeTab, setActiveTab] = useState('positive');
  const [selCell,   setSelCell]   = useState(null);
  const [corrState, setCorrState] = useState(null); // null = computing

  const types   = useMemo(()=>inferTypes(headers,rows),[rows,headers]);
  const numCols = useMemo(()=>types.filter(t=>t.type==='numeric').map(t=>t.col),[types]);
  const corrRows = useMemo(()=>sampleRows(rows,MAX_CORR_PTS),[rows]);

  // Heavy: pearson matrix + all pairs — async so spinner shows first
  useEffect(() => {
    // Use precomputed state when available (View Report mode — full dataset)
    if (precomputedCorrState) { setCorrState(precomputedCorrState); return; }
    if (numCols.length < 2) return;
    setCorrState(null);
    const t = setTimeout(() => {
      const matCols = numCols.slice(0, 14);
      const matrix  = matCols.map(a => matCols.map(b => pearson(corrRows, a, b)));
      const allPairs = [];
      for (let i = 0; i < numCols.length; i++)
        for (let j = i + 1; j < numCols.length; j++)
          allPairs.push({ a: numCols[i], b: numCols[j], r: pearson(corrRows, numCols[i], numCols[j]) });
      allPairs.sort((a, b) => Math.abs(b.r) - Math.abs(a.r));
      setCorrState({ matCols, matrix, allPairs });
    }, 0);
    return () => clearTimeout(t);
  }, [corrRows, numCols.join(','), precomputedCorrState]); // eslint-disable-line

  const selR = selCell && corrState
    ? pearson(corrRows, selCell.a, selCell.b)
    : 0;

  const TABS=[
    {id:'positive',label:'TOP POSITIVE',color:C.green},
    {id:'negative',label:'TOP NEGATIVE',color:C.red},
    {id:'heatmap',label:'HEATMAP',color:C.cyan},
    {id:'gallery',label:'SCATTER GALLERY',color:C.violet},
  ];

  const matCols = corrState?.matCols || [];
  const matrix = corrState?.matrix || [];
  const allPairs = corrState?.allPairs || [];
  const pairLookup = useMemo(() => {
    const lookup = {};
    allPairs.forEach(pair => {
      lookup[`${pair.a}__${pair.b}`] = pair;
      lookup[`${pair.b}__${pair.a}`] = { ...pair, a: pair.b, b: pair.a };
    });
    return lookup;
  }, [allPairs]);

  useEffect(() => {
    if (activeTab !== 'heatmap' || selCell || !allPairs.length) return;
    setSelCell({ a: allPairs[0].a, b: allPairs[0].b });
  }, [activeTab, selCell, allPairs]);

  const selectedPair = selCell
    ? (pairLookup[`${selCell.a}__${selCell.b}`] || {
        a: selCell.a,
        b: selCell.b,
        r: matrix[matCols.indexOf(selCell.a)]?.[matCols.indexOf(selCell.b)] ?? 0,
        pts: buildScatterPts(rows, selCell.a, selCell.b, MAX_SCATTER),
      })
    : null;

  // For heatmap scatter pane: use precomputed pts when available
  const selPts = useMemo(() => {
    const normalizedPts = normalizeScatterPts(selectedPair?.pts);
    if (normalizedPts.length) return normalizedPts;
    if (!selectedPair?.a || !selectedPair?.b) return null;
    return buildScatterPts(rows, selectedPair.a, selectedPair.b, MAX_SCATTER);
  }, [selectedPair, rows]);

  if(numCols.length<2) return (
    <div style={{padding:40,textAlign:'center',color:C.t4,fontSize:12}}>
      Need at least 2 numeric columns for correlation analysis.
    </div>
  );
  if(!corrState) return <LabLoader rows={rows.length} label="COMPUTING CORRELATIONS" />;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      {/* Tab bar */}
      <div style={{display:'flex',gap:2,background:C.deep,border:`1px solid ${C.b0}`,borderRadius:4,padding:3,flexWrap:'wrap'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{
            padding:'6px 14px',border:'none',borderRadius:3,cursor:'pointer',
            background:activeTab===t.id?t.color:'transparent',
            color:activeTab===t.id?C.void:C.t2,
            fontFamily:'JetBrains Mono,monospace',fontSize:10,fontWeight:700,letterSpacing:'.08em',
          }}>{t.label}</button>
        ))}
        <span style={{marginLeft:'auto',fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace',alignSelf:'center',paddingRight:6}}>
          ☆ pin any chart to dashboard
        </span>
      </div>

      {/* HEATMAP */}
      {activeTab==='heatmap'&&(
        <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
          <div style={{flex:2,minWidth:300,overflowX:'auto'}}>
            <table style={{borderCollapse:'separate',borderSpacing:2}}>
              <thead>
                <tr>
                  <th style={{padding:'3px 6px',color:C.t4,fontSize:8}}/>
                  {matCols.map(c=>(
                    <th key={c} style={{padding:'3px 6px',color:C.t3,fontSize:8,fontWeight:700}}>
                      {c.length>8?c.slice(0,7)+'…':c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matCols.map((row,ri)=>(
                  <tr key={row}>
                    <td style={{padding:'3px 6px',color:C.t3,fontSize:8,fontWeight:700,whiteSpace:'nowrap'}}>
                      {row.length>8?row.slice(0,7)+'…':row}
                    </td>
                    {matCols.map((col,ci)=>{
                      const v=matrix[ri]?.[ci]??0;
                      const abs=Math.abs(v);
                      const bg=ri===ci?C.b1:v>0?`rgba(74,222,128,${abs*.85})`:`rgba(248,113,113,${abs*.85})`;
                      const isHL=selCell&&((selCell.a===row&&selCell.b===col)||(selCell.a===col&&selCell.b===row));
                      return (
                        <td key={col} onClick={()=>ri!==ci&&setSelCell({a:row,b:col})}
                          style={{
                            padding:'4px 6px',background:bg,textAlign:'center',minWidth:38,
                            color:abs>0.5?C.void:C.t1,borderRadius:2,
                            cursor:ri!==ci?'pointer':'default',
                            outline:isHL?`2px solid ${C.gold}`:'none',
                          }}>{v.toFixed(2)}</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{marginTop:8,display:'flex',gap:10,fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace'}}>
              <span style={{color:C.green}}>■ Positive</span>
              <span style={{color:C.red}}>■ Negative</span>
              <span style={{color:C.gold}}>■ Selected · click cell → scatter</span>
              {rows.length>MAX_CORR_PTS&&<span style={{color:C.gold}}>⚡ r on {MAX_CORR_PTS.toLocaleString()} sample rows</span>}
            </div>
          </div>

          {/* Scatter pane */}
          <div style={{flex:1,minWidth:280}}>
            {selCell?(
              <div style={{background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,overflow:'hidden'}}>
                <div style={{padding:'8px 14px',background:C.deep,borderBottom:`1px solid ${C.b0}`,
                  display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <span style={{fontSize:10,color:rColor(selR),letterSpacing:'.12em',fontWeight:700,fontFamily:'JetBrains Mono,monospace'}}>
                    {selectedPair?.a || selCell.a} × {selectedPair?.b || selCell.b}
                  </span>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <PinButton chart={{ id:`corr_hm_${selectedPair?.a || selCell.a}_${selectedPair?.b || selCell.b}`, type:'scatter', xCol:selectedPair?.a || selCell.a, yCol:selectedPair?.b || selCell.b, title:`${selectedPair?.a || selCell.a} vs ${selectedPair?.b || selCell.b}`, subtitle:`r=${selR.toFixed(3)} · heatmap` }} small />
                    <span style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:22,color:rColor(selR)}}>{selR.toFixed(3)}</span>
                  </div>
                </div>
                <div style={{padding:'10px 14px 14px'}}>
                  <MiniScatter rows={rows} xCol={selectedPair?.a || selCell.a} yCol={selectedPair?.b || selCell.b} color={rColor(selR)} height={200} precomputedPts={selPts}/>
                </div>
                <div style={{padding:'0 14px 12px',fontSize:10,color:C.t3}}>{rStrong(selR)}</div>
              </div>
            ):(
              <div style={{padding:40,textAlign:'center',color:C.t4}}>
                <div style={{fontSize:24,opacity:0.3,marginBottom:8}}>⬡</div>
                <div style={{fontSize:11}}>Click a cell to see scatter</div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab==='positive'&&<CorrDashboard rows={rows} allPairs={allPairs} direction="positive"/>}
      {activeTab==='negative'&&<CorrDashboard rows={rows} allPairs={allPairs} direction="negative"/>}
      {activeTab==='gallery'&&<ScatterGallery rows={rows} allPairs={allPairs}/>}
    </div>
  );
}
